import type { LoginRequest } from '@/types';
import type { AuthProvider } from '@refinedev/core';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { buildLoginPath } from '@/utils/loginPath';
import { getInternalRedirectPath } from '@/utils/redirectPath';
import { ROUTES } from '@/constants/routes';
import {
  clearAuthSession,
  getCurrentUser,
  getStoredUser,
  getToken,
  isTokenExpired,
  login as authLogin,
  logout as authLogout,
  refreshAccessToken,
} from '@/services/auth';

// check()'s error name for a session that could not be refreshed. LoginRedirect
// reads it to show Login's "session ended" notice.
export const TOKEN_EXPIRED_ERROR_NAME = 'TokenExpired';

export const authProvider: AuthProvider = {
  login: async ({ email, password }: LoginRequest) => {
    try {
      const session = await authLogin({ email, password });

      // Honor the intended route that <CatchAllNavigate> captured as ?to= on
      // /login (e.g. a deep link opened while unauthenticated), falling back to
      // the app root. Without this, every post-login redirect went to '/'
      // regardless of where the user was originally headed.
      const to = getInternalRedirectPath(new URLSearchParams(window.location.search).get('to'));

      // A password someone else set must be changed before any other route answers;
      // the change page takes the user on to `to` afterwards.
      if (session.user.mustChangePassword === true) {
        return {
          success: true,
          redirectTo: to
            ? `${ROUTES.CHANGE_PASSWORD}?${new URLSearchParams({ to }).toString()}`
            : ROUTES.CHANGE_PASSWORD,
        };
      }

      return {
        success: true,
        redirectTo: to ?? '/',
      };
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: getApiErrorMessage(error, 'Invalid email or password'),
        },
      };
    }
  },

  logout: async () => {
    await authLogout();
    return {
      success: true,
      redirectTo: '/login',
    };
  },

  check: async () => {
    const token = getToken();

    if (!token) {
      return {
        authenticated: false,
        redirectTo: '/login',
        error: {
          name: 'Unauthorized',
          message: 'No authentication token found',
        },
      };
    }

    // Check if token is expired
    if (isTokenExpired()) {
      try {
        // Try to refresh the token
        await refreshAccessToken();
      } catch {
        clearAuthSession();
        return {
          authenticated: false,
          redirectTo: '/login',
          error: {
            name: TOKEN_EXPIRED_ERROR_NAME,
            message: 'Session expired, please login again',
          },
        };
      }
    }

    return {
      authenticated: true,
    };
  },

  getPermissions: () => {
    const user = getStoredUser();
    return Promise.resolve(user?.role ? [user.role] : []);
  },

  getIdentity: async () => {
    try {
      // Try to get fresh user data from API
      const user = await getCurrentUser();
      return {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        rental: user.rental,
        avatar: undefined,
      };
    } catch {
      // Fall back to stored user data
      const user = getStoredUser();
      if (!user) return null;

      return {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        rental: user.rental,
        avatar: undefined,
      };
    }
  },

  onError: async (error) => {
    // Handle 401 errors by attempting token refresh
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (error.response?.status === 401) {
      try {
        await refreshAccessToken();
        // Token refreshed successfully, retry the request
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        return { error };
      } catch {
        // Refresh failed, clear session and redirect to login
        clearAuthSession();
        return {
          logout: true,
          redirectTo: buildLoginPath(`${window.location.pathname}${window.location.search}`, {
            sessionEnded: true,
          }),
          error: {
            name: 'Unauthorized',
            message: 'Session expired, please login again',
          },
        };
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return { error };
  },
};
